/**
 * API Service for communicating with the Express backend
 * All financial calculations are done server-side
 */

// Empty base URL - endpoints in config already include '/api/' prefix
const API_BASE = '';

interface ApiError {
  message: string;
  status: number;
}

class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = endpoint.startsWith('http')
      ? endpoint
      : `${this.baseUrl}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw {
          message: errorData.message || `HTTP error ${response.status}`,
          status: response.status,
        } as ApiError;
      }

      // Handle blob responses (for Excel downloads)
      const contentType = response.headers.get('content-type');
      if (
        contentType?.includes('application/vnd.openxmlformats') ||
        contentType?.includes('application/octet-stream')
      ) {
        return (await response.blob()) as unknown as T;
      }

      return await response.json();
    } catch (error) {
      if ((error as ApiError).status) {
        throw error;
      }
      throw {
        message: 'Network error. Please check your connection.',
        status: 0,
      } as ApiError;
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Download Excel file
  async downloadExcel(
    endpoint: string,
    data: unknown,
    filename: string
  ): Promise<void> {
    const blob = await this.post<Blob>(endpoint, data);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

// Singleton instance
export const api = new ApiService();

// Model-specific API helpers
// Base URL is empty, so endpoints need the full '/api' prefix
export const modelApi = {
  // Generic calculate endpoint
  calculate: async <T>(modelType: string, assumptions: Record<string, unknown>): Promise<T> => {
    return api.post<T>(`/api/${modelType}/calculate`, { assumptions });
  },

  // Generic Excel export
  exportExcel: async (modelType: string, assumptions: Record<string, unknown>): Promise<void> => {
    return api.downloadExcel(
      `/api/${modelType}/export`,
      { assumptions },
      `${modelType}-financial-model.xlsx`
    );
  },

  // Get default assumptions
  getDefaults: async <T>(modelType: string): Promise<T> => {
    return api.get<T>(`/api/${modelType}/assumptions`);
  },
};

export default api;
